<template>
    <div class="monitor-details-page">
        <!-- Page Header -->
        <div class="monitor-header">
            <div class="d-flex align-center justify-space-between">
                <h1 class="monitor-title">{{ monitorId }}</h1>
                <v-menu>
                    <template v-slot:activator="{ props }">
                        <Button icon="pi pi-ellipsis-v" severity="secondary" text rounded v-bind="props" aria-label="Actions" />
                    </template>
                    <v-list>
                        <v-list-item button title="Report a Failure" @click="showReportAFailureModal = true">
                            <template v-slot:prepend>
                                <v-icon color="red-darken-2" icon="mdi-message-alert" class="mr-2"></v-icon>
                            </template>
                        </v-list-item>
                        <v-list-item button title="View Chem Check Info" @click="showChemCheckInfoModal = true">
                            <template v-slot:prepend>
                                <v-icon icon="mdi-flask-outline" class="mr-2" color="indigo"></v-icon>
                            </template>
                        </v-list-item>
                        <v-list-item button title="Live Samples" :to="{ name: 'MonitorLiveStream', params: { monitorId } }">
                            <template v-slot:prepend>
                                <v-icon icon="mdi-chart-timeline-variant" class="mr-2" color="teal"></v-icon>
                            </template>
                        </v-list-item>
                        <v-divider></v-divider>
                        <v-list-subheader>AWS</v-list-subheader>
                        <v-list-item button title="CloudWatch Logs" target="_aws" :href="monitorDetails?.cloudWatchLogsUrl">
                            <template v-slot:prepend>
                                <v-icon color="warning" icon="mdi-aws" class="mr-2"></v-icon>
                            </template>
                        </v-list-item>
                        <v-list-item button title="CloudWatch Samples" target="_aws" :href="monitorDetails?.cloudWatchSamplesUrl">
                            <template v-slot:prepend>
                                <v-icon color="warning" icon="mdi-aws" class="mr-2"></v-icon>
                            </template>
                        </v-list-item>
                        <v-list-item
                            button
                            title="View on Core IoT"
                            target="_aws"
                            :href="`https://us-east-1.console.aws.amazon.com/iot/home?region=us-east-1#/thing/${monitorId}`"
                        >
                            <template v-slot:prepend>
                                <v-icon color="warning" icon="mdi-aws" class="mr-2"></v-icon>
                            </template>
                        </v-list-item>
                        <template v-if="spaConfigId">
                            <v-divider></v-divider>
                            <v-list-subheader>Spa Pack</v-list-subheader>
                            <v-list-item button title="Browse Config Files" @click="openSpaConfigFiles">
                                <template v-slot:prepend>
                                    <v-icon icon="mdi-folder-open" class="mr-2" color="blue"></v-icon>
                                </template>
                            </v-list-item>
                        </template>
                    </v-list>
                </v-menu>
            </div>
        </div>

        <v-snackbar v-model="errorSnackBar" multi-line>{{ errorText }}</v-snackbar>

        <!-- Loading -->
        <div v-if="loadingMonitorDetails" class="d-flex justify-center py-16">
            <ProgressSpinner />
        </div>

        <div v-else-if="monitorDetails" class="monitor-content">
            <!-- Tags -->
            <div class="mb-4">
                <ResourceTags resource-type="monitor" :resource-id="monitorId" />
            </div>

            <!-- Last Job Execution Failed Banner -->
            <Message v-if="lastFailedExecution" severity="error" :closable="false" class="mb-4">
                <template #default>
                    <div class="d-flex align-center flex-wrap" style="gap: 12px">
                        <span>
                            Last job execution
                            <router-link
                                :to="{
                                    name: 'MonitorJobExecutionDetails',
                                    params: { monitorId, jobId: lastFailedExecution.jobId, executionNumber: lastFailedExecution.executionNumber }
                                }"
                            ><strong>{{ lastFailedExecution.jobId }}</strong></router-link>
                            {{ lastFailedExecution.status.toLowerCase().replace('_', ' ') }}<template v-if="lastFailedExecution.lastUpdatedAtDate">
                                — {{ format(lastFailedExecution.lastUpdatedAtDate, 'M/d/yy h:mmaaaaa') }}
                            </template>
                        </span>
                        <span v-if="lastFailedExecution.statusDetails && Object.keys(lastFailedExecution.statusDetails).length">
                            — {{ lastFailedExecution.statusDetails.errorCode != null ? getOtaErrorMessage(lastFailedExecution.statusDetails.errorCode) : Object.entries(lastFailedExecution.statusDetails).map(([k, v]) => `${k}: ${v}`).join(', ') }}
                        </span>
                    </div>
                </template>
            </Message>

            <!-- General Info + Manufacturing Info -->
            <v-row class="mb-4">
                <v-col cols="12" md="6">
                    <Card class="h-100">
                        <template #title>General Info</template>
                        <template #content>
                            <div class="info-grid">
                                <div class="info-row">
                                    <span class="info-label">Account</span>
                                    <span class="info-value">
                                        <account-button v-if="monitorDetails.owningAccountId" :account-id="monitorDetails.owningAccountId" />
                                        <span v-else class="text-color-secondary">—</span>
                                    </span>
                                </div>
                                <div v-if="monitorDetails.vessel" class="info-row">
                                    <span class="info-label">Vessel</span>
                                    <span class="info-value"><vessel-button :vessel="monitorDetails.vessel" /></span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">Sensor</span>
                                    <span class="info-value">
                                        <sensor-button
                                            v-if="monitorDetails.sensor"
                                            :sensor="monitorDetails.sensor"
                                            :monitor-id="monitorId"
                                            @sensor-detached="sensorDetached"
                                        />
                                        <v-dialog v-else v-model="showAttachSensor">
                                            <template v-slot:activator="{ props: dialogProps }">
                                                <Button v-bind="dialogProps" icon="pi pi-plus" label="Attach Sensor" severity="secondary" outlined size="small" />
                                            </template>
                                            <v-card :min-width="400">
                                                <v-card-title>Search for a sensor to attach</v-card-title>
                                                <v-card-text>
                                                    <choose-sensor @picked:sensor="pickedSensor" />
                                                </v-card-text>
                                                <v-card-actions>
                                                    <v-btn color="secondary" @click="showAttachSensor = false">Cancel</v-btn>
                                                </v-card-actions>
                                            </v-card>
                                        </v-dialog>
                                    </span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">Provisioning</span>
                                    <span class="info-value">
                                        <Tag v-if="monitorDetails.provisioningStatus === 'provisioned'" severity="info" value="PROVISIONED" />
                                        <Tag v-else-if="monitorDetails.provisioningStatus === 'provisioning'" severity="warn" value="PROVISIONING" />
                                        <Tag v-else severity="secondary" value="UNKNOWN" />
                                        <span v-if="monitorDetails.provisioningStatusUpdatedAt" class="ml-2 text-color-secondary" style="font-size: 0.8rem">
                                            since {{ format(monitorDetails.provisioningStatusUpdatedAt, 'Pp') }}
                                        </span>
                                    </span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">Calibrations</span>
                                    <span class="info-value">
                                        <Button
                                            v-if="monitorDetails.stateShadow?.state.reported?.features?.waterlab?.sensor"
                                            label="View Calibrations"
                                            size="small"
                                            severity="secondary"
                                            outlined
                                            @click="router.push({ name: 'MonitorCalibrations', params: { monitorId } })"
                                        />
                                        <Button
                                            v-else
                                            label="Add Calibrations"
                                            size="small"
                                            severity="secondary"
                                            outlined
                                            @click="router.push({ name: 'MonitorCalibrations', params: { monitorId } })"
                                        />
                                    </span>
                                </div>
                                <div v-if="monitorDetails.lastSample && monitorDetails.lastSampleDate" class="info-row">
                                    <span class="info-label">Last Sample <span class="text-color-secondary" style="font-size: 0.8rem">(<time-ago-with-date :date="monitorDetails.lastSampleDate" />)</span></span>
                                    <span class="info-value" style="font-size: 0.85rem">{{ formattedLastSample }}</span>
                                </div>
                            </div>
                        </template>
                    </Card>
                </v-col>
                <v-col cols="12" md="6">
                    <Card class="h-100">
                        <template #title>
                            <div class="d-flex align-center justify-space-between flex-wrap" style="gap: 8px">
                                <span>Device &amp; Manufacturing</span>
                                <div class="d-flex" style="gap: 8px">
                                    <router-link :to="geckoSpaRoute" class="nav-pill nav-pill--gecko">
                                        <img src="/gecko-logo-white.svg" alt="Gecko logo" class="nav-pill__icon" />
                                        <span>Spa Control</span>
                                    </router-link>
                                    <router-link v-if="showAnalyticsCard" :to="analyticsRoute!" class="nav-pill nav-pill--analytics">
                                        <i class="pi pi-chart-line" style="font-size: 0.8rem"></i>
                                        <span>Analytics</span>
                                    </router-link>
                                </div>
                            </div>
                        </template>
                        <template #content>
                            <div class="info-grid">
                                <div v-if="monitorDetails.oemId" class="info-row">
                                    <span class="info-label">Manufacturer</span>
                                    <span class="info-value">{{ monitorDetails.oemId }}</span>
                                </div>
                                <div v-if="monitorDetails.model" class="info-row">
                                    <span class="info-label">Model</span>
                                    <span class="info-value">{{ monitorDetails.model }}</span>
                                </div>
                                <div v-if="monitorDetails.hardwareRevision" class="info-row">
                                    <span class="info-label">Hardware Rev.</span>
                                    <span class="info-value">{{ monitorDetails.hardwareRevision }}</span>
                                </div>
                                <div v-if="manufacturingDate" class="info-row">
                                    <span class="info-label">Manufacturing Date</span>
                                    <span class="info-value">{{ format(manufacturingDate, 'MMM d, yyyy') }}</span>
                                </div>
                                <Divider class="my-2" />
                                <FirmwareOtaRow
                                    label="EN Firmware"
                                    :current-version="monitorDetails.identityShadow?.state.reported?.en_?.fwVersion"
                                    :monitor-id="monitorId"
                                    firmware-type="EN"
                                    :catalog="otaCatalog"
                                    :shadow="monitorDetails.identityShadow?.state.reported?.en_"
                                    :disabled="hasActiveJob"
                                    @ota-sent="onOtaSent"
                                />
                                <FirmwareOtaRow
                                    label="CO Firmware"
                                    :current-version="monitorDetails.identityShadow?.state.reported?.co_?.fwVersion"
                                    :monitor-id="monitorId"
                                    firmware-type="CO"
                                    :catalog="otaCatalog"
                                    :shadow="monitorDetails.identityShadow?.state.reported?.co_"
                                    :disabled="hasActiveJob"
                                    @ota-sent="onOtaSent"
                                />
                                <FirmwareOtaRow
                                    label="Waterlab Firmware"
                                    :current-version="monitorDetails.identityShadow?.state.reported?.waterlab_?.fwVersion"
                                    :monitor-id="monitorId"
                                    firmware-type="WL"
                                    :catalog="otaCatalog"
                                    :shadow="monitorDetails.identityShadow?.state.reported?.waterlab_"
                                    :disabled="hasActiveJob"
                                    @ota-sent="onOtaSent"
                                />
                                <div class="info-row">
                                    <span class="info-label">Spa Config ID</span>
                                    <span class="info-value"><code>{{ monitorDetails.identityShadow?.state.reported?.spaConfigId_ ?? '—' }}</code></span>
                                </div>
                                <div v-if="monitorDetails?.sensor?.serialNumber" class="info-row">
                                    <span class="info-label">Shadow Sensor S/N</span>
                                    <span class="info-value"><code>{{ monitorDetails.sensor.serialNumber }}</code></span>
                                </div>
                                <template v-if="sortedCapabilities && sortedCapabilities.length > 0">
                                    <Divider class="my-2" />
                                    <div class="info-row info-row--top">
                                        <span class="info-label">Capabilities</span>
                                        <span class="info-value">
                                            <div class="d-flex flex-wrap" style="gap: 6px">
                                                <Tag
                                                    v-for="cap of sortedCapabilities"
                                                    :key="cap.title"
                                                    :severity="cap.status ? 'success' : 'danger'"
                                                    :value="cap.title"
                                                />
                                            </div>
                                        </span>
                                    </div>
                                </template>
                            </div>
                        </template>
                    </Card>
                </v-col>
            </v-row>

            <!-- Reported Failures -->
            <div v-if="loadingMonitorFailures" class="d-flex justify-center py-8">
                <ProgressSpinner />
            </div>
            <Card v-else-if="monitorFailures !== undefined && monitorFailures.length > 0" class="mb-4">
                <template #title>Reported Failures</template>
                <template #content>
                    <DataTable :value="monitorFailures" stripedRows size="small" paginator :rows="5" :rowsPerPageOptions="[5, 10, 20]">
                        <Column header="Failure Type">
                            <template #body="{ data }">
                                <monitor-failure-type-chip :failure-type="data.failureType" />
                            </template>
                        </Column>
                        <Column header="Initial Activation">
                            <template #body="{ data }">
                                {{ format(data.initialActivationDate, 'M/d/yy h:mmaaaaa z', { timeZone: timezone.zone }) }}
                            </template>
                        </Column>
                        <Column header="Failure Start">
                            <template #body="{ data }">
                                {{ format(data.failureStartDate, 'M/d/yy h:mmaaaaa z', { timeZone: timezone.zone }) }}
                            </template>
                        </Column>
                        <Column field="timeInUse" header="Time In Use" />
                        <Column field="status" header="Status" />
                        <Column field="location" header="Location" />
                        <Column field="notes" header="Notes" />
                    </DataTable>
                </template>
            </Card>

            <!-- Sensor History -->
            <Card class="mb-4">
                <template #title>Sensor History</template>
                <template #content>
                    <monitor-sensor-history ref="sensorHistory" :monitor-id="monitorId" />
                </template>
            </Card>

            <!-- Jobs -->
            <template v-if="monitorJobs?.pending?.length || monitorJobs?.executions?.length">
                <Card v-if="monitorJobs?.pending?.length" class="mb-4">
                    <template #title>
                        <div class="d-flex align-center justify-space-between">
                            <span>Pending Jobs</span>
                            <Button icon="pi pi-refresh" severity="secondary" text rounded size="small" :loading="loadingMonitorJobs" @click="getMonitorJobs" aria-label="Refresh jobs" />
                        </div>
                    </template>
                    <template #content>
                        <DataTable :value="monitorJobs.pending" stripedRows size="small" paginator :rows="5" :rowsPerPageOptions="[5, 10, 20]">
                            <Column field="jobId" header="Job ID" />
                            <Column field="status" header="Status" />
                            <Column header="Queued">
                                <template #body="{ data }">
                                    {{ data.queuedAtDate ? format(data.queuedAtDate, 'M/d/yy h:mmaaaaa') : '—' }}
                                </template>
                            </Column>
                            <Column header="Started">
                                <template #body="{ data }">
                                    {{ data.startedAtDate ? format(data.startedAtDate, 'M/d/yy h:mmaaaaa') : '—' }}
                                </template>
                            </Column>
                            <Column header="Last Updated">
                                <template #body="{ data }">
                                    {{ data.lastUpdatedAtDate ? format(data.lastUpdatedAtDate, 'M/d/yy h:mmaaaaa') : '—' }}
                                </template>
                            </Column>
                            <Column header="">
                                <template #body="{ data }">
                                    <Button label="View on AWS" icon="pi pi-external-link" size="small" severity="warn" outlined :href="data.awsJobUrl" as="a" target="_aws" />
                                </template>
                            </Column>
                        </DataTable>
                    </template>
                </Card>

                <Card v-if="monitorJobs?.executions?.length" class="mb-4">
                    <template #title>
                        <div class="d-flex align-center justify-space-between">
                            <span>Job Executions</span>
                            <Button v-if="!monitorJobs?.pending?.length" icon="pi pi-refresh" severity="secondary" text rounded size="small" :loading="loadingMonitorJobs" @click="getMonitorJobs" aria-label="Refresh jobs" />
                        </div>
                    </template>
                    <template #content>
                        <DataTable :value="monitorJobs.executions" stripedRows size="small" paginator :rows="5" :rowsPerPageOptions="[5, 10, 20]">
                            <Column header="Job ID">
                                <template #body="{ data }">
                                    <router-link
                                        :to="{
                                            name: 'MonitorJobExecutionDetails',
                                            params: { monitorId, jobId: data.jobId, executionNumber: data.executionNumber }
                                        }"
                                    >{{ data.jobId }}</router-link>
                                </template>
                            </Column>
                            <Column field="status" header="Status" />
                            <Column header="Error">
                                <template #body="{ data }">
                                    <span v-if="data.statusDetails?.errorCode" class="text-red-500">
                                        {{ getOtaErrorMessage(data.statusDetails.errorCode) }}
                                    </span>
                                    <span v-else>—</span>
                                </template>
                            </Column>
                            <Column header="Queued">
                                <template #body="{ data }">
                                    {{ data.queuedAtDate ? format(data.queuedAtDate, 'M/d/yy h:mmaaaaa') : '—' }}
                                </template>
                            </Column>
                            <Column header="Started">
                                <template #body="{ data }">
                                    {{ data.startedAtDate ? format(data.startedAtDate, 'M/d/yy h:mmaaaaa') : '—' }}
                                </template>
                            </Column>
                            <Column header="Last Updated">
                                <template #body="{ data }">
                                    {{ data.lastUpdatedAtDate ? format(data.lastUpdatedAtDate, 'M/d/yy h:mmaaaaa') : '—' }}
                                </template>
                            </Column>
                            <Column header="">
                                <template #body="{ data }">
                                    <Button label="View on AWS" icon="pi pi-external-link" size="small" severity="warn" outlined :href="data.awsJobUrl" as="a" target="_aws" />
                                </template>
                            </Column>
                        </DataTable>
                    </template>
                </Card>
            </template>

            <!-- Vessel History -->
            <Card class="mb-4">
                <template #title>Vessel History</template>
                <template #content>
                    <template v-if="monitorDetails.attachmentHistory && monitorDetails.attachmentHistory.length > 0">
                        <DataTable :value="monitorDetails.attachmentHistory" stripedRows size="small" paginator :rows="5" :rowsPerPageOptions="[5, 10, 20]">
                            <Column header="Start">
                                <template #body="{ data }">
                                    {{ data.startDate && format(data.startDate, 'Pp') }}
                                </template>
                            </Column>
                            <Column header="End">
                                <template #body="{ data }">
                                    {{ data.current ? 'current' : data.endDate && format(data.endDate, 'Pp') }}
                                </template>
                            </Column>
                            <Column header="Duration">
                                <template #body="{ data }">
                                    {{ duration(data) }}
                                </template>
                            </Column>
                            <Column header="Vessel">
                                <template #body="{ data }">
                                    <vessel-button v-if="data.vessel" :vessel="data.vessel" />
                                </template>
                            </Column>
                        </DataTable>
                        <div class="d-flex justify-end mt-3" style="font-size: 0.875rem">
                            <span class="text-color-secondary mr-2">Total Duration:</span>
                            <strong>{{ totalTimeInWater }}</strong>
                        </div>
                    </template>
                    <div v-else class="text-center py-6 text-color-secondary">No Vessel History</div>
                </template>
            </Card>
        </div>

        <!-- Modals -->
        <ConfirmDialog />

        <report-monitor-failure-modal v-model="showReportAFailureModal" :monitor-id="monitorId" @reported-failure="reportedFailure" />
        <chem-check-info-modal v-model="showChemCheckInfoModal" :monitor-id="monitorId" />

        <!-- Spa Config Files Dialog -->
        <SpaConfigFilesDialog v-if="spaConfigId" v-model="showSpaConfigFiles" :config-id="spaConfigId" @error="onSpaConfigFilesError" />
    </div>
</template>

<script setup lang="ts">
import { useApi } from '@/api'
import { ref, onMounted, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import '@/components/VesselDisc.vue'
import { useBreadCrumbsStore } from '@/stores/breadCrumbs'
import { differenceInSeconds, formatDuration, intervalToDuration } from 'date-fns'
import { format } from 'date-fns-tz'
import type {
    GetSensorSearchResultV1,
    MonitorDetailsV1,
    MonitorDetailsAttachmentHistoryV1,
    MonitorFailureV1,
    MonitorJobsV1,
    MonitorCapabilitiesV1,
    MonitorSampleV1,
    OTAOptionsV1
} from '@geckoal/gecko-api-client'
import { JobExecutionStatesV1 } from '@geckoal/gecko-api-client'
import VesselButton from '@/components/buttons/VesselButton.vue'
import { handleApiError, requireTimezone, camelCaseToSentence } from '@/lib/utils'
import { getOtaErrorMessage } from '@/lib/monitorErrors'
import ChooseSensor from '@/components/sensors/ChooseSensor.vue'
import MonitorSensorHistory from '@/components/monitors/MonitorSensorHistory.vue'
import ReportMonitorFailureModal from '@/components/monitors/ReportMonitorFailureModal.vue'
import SensorButton from '@/components/buttons/SensorButton.vue'
import AccountButton from '@/components/buttons/AccountButton.vue'
import MonitorFailureTypeChip from '@/components/monitors/MonitorFailureTypeChip.vue'
import ChemCheckInfoModal from '@/components/debug/ViewChemCheckInfoModal.vue'
import FirmwareOtaRow from '@/components/monitors/FirmwareOtaRow.vue'
import SpaConfigFilesDialog from '@/components/monitors/SpaConfigFilesDialog.vue'
import TimeAgoWithDate from '@/components/TimeAgoWithDate.vue'
import ResourceTags from '@/components/tags/ResourceTags.vue'
import Card from 'primevue/card'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import ProgressSpinner from 'primevue/progressspinner'
import Divider from 'primevue/divider'
import Message from 'primevue/message'
import ConfirmDialog from 'primevue/confirmdialog'

const breadCrumbsStore = useBreadCrumbsStore()
const router = useRouter()
const timezone = requireTimezone()

const props = defineProps({
    monitorId: {
        type: String,
        required: true
    }
})

const errorSnackBar = ref(false)
const errorText = ref<string | undefined>()
const loadingMonitorDetails = ref(false)
const monitorDetails = ref<MonitorDetailsV1 | undefined>()

const loadingMonitorJobs = ref(false)
const monitorJobs = ref<MonitorJobsV1 | undefined>()
const showAttachSensor = ref(false)

const FAILED_STATUSES = new Set([JobExecutionStatesV1.Failed, JobExecutionStatesV1.TimedOut, JobExecutionStatesV1.Rejected])

const lastFailedExecution = computed(() => {
    const executions = monitorJobs.value?.executions
    if (!executions?.length) return undefined
    const sorted = [...executions].sort((a, b) => {
        const dateA = a.lastUpdatedAtDate?.getTime() ?? 0
        const dateB = b.lastUpdatedAtDate?.getTime() ?? 0
        return dateB - dateA
    })
    const latest = sorted[0]
    return FAILED_STATUSES.has(latest.status) ? latest : undefined
})
const sensorHistory = ref<InstanceType<typeof MonitorSensorHistory>>()

const loadingMonitorFailures = ref(false)
const monitorFailures = ref<MonitorFailureV1[] | undefined>()
const showReportAFailureModal = ref(false)
const showChemCheckInfoModal = ref(false)

// --- OTA catalog ---
type OtaCatalog = OTAOptionsV1['catalog']
const otaCatalog = ref<OtaCatalog>({})
const otaSentPending = ref(false)

const hasActiveJob = computed(() => {
    return otaSentPending.value || !!monitorJobs.value?.pending?.length
})

const loadOtaOptions = async () => {
    try {
        const result = await useApi().getOTAOptionsV1({ monitorId: props.monitorId })
        otaCatalog.value = result.catalog ?? {}
    } catch (e) {
        console.error('Error loading OTA options', e)
    }
}

const formattedLastSample = computed(() => {
    const lastSample = monitorDetails.value?.lastSample
    if (lastSample === undefined) return undefined

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { ph, orpMv, waterTempC, sampleDate: _sampleDate, monitorId: _monitorId, ...others } = lastSample

    const pairs: string[] = []
    if (ph) pairs.push(`ph=${ph}`)
    if (orpMv) pairs.push(`orp=${orpMv}`)
    if (waterTempC) pairs.push(`temp=${waterTempC}`)

    for (const key of Object.keys(others) as (keyof MonitorSampleV1)[]) {
        const value = (others as Record<string, unknown>)[key]
        if (value !== undefined) pairs.push(`${key}=${value}`)
    }

    return pairs.join(', ')
})

const showAnalyticsCard = computed(() => {
    return !!import.meta.env.VITE_ANALYTICS_SINGLE_VESSEL_URL
})

const geckoSpaRoute = computed(() => {
    return { name: 'MonitorGeckoSpaStatus', params: { monitorId: props.monitorId } }
})

const analyticsRoute = computed(() => {
    if (!showAnalyticsCard.value) return undefined
    return { name: 'MonitorAnalytics', params: { monitorId: props.monitorId } }
})

const manufacturingDate = computed(() => {
    const id = props.monitorId
    if (!id || id.length !== 9) return undefined
    const yearPart = Number.parseInt(id.substring(0, 2), 10)
    const dayPart = Number.parseInt(id.substring(2, 5), 10)
    if (Number.isNaN(yearPart) || Number.isNaN(dayPart) || dayPart < 1 || dayPart > 366) return undefined
    const year = 2000 + yearPart
    const date = new Date(year, 0, dayPart)
    if (date.getFullYear() !== year) return undefined
    return date
})

const sortedCapabilities = computed(() => {
    if (monitorDetails.value === undefined) return undefined

    return Object.keys(monitorDetails.value.capabilities)
        .map((key) => {
            const value = monitorDetails.value?.capabilities[key as keyof MonitorCapabilitiesV1]
            return {
                title: camelCaseToSentence(key),
                status: value
            }
        })
        .sort((a, b) => a.title.localeCompare(b.title))
})

const spaConfigId = computed(() => {
    return monitorDetails.value?.identityShadow?.state?.reported?.spaConfigId_
})

const showSpaConfigFiles = ref(false)

const openSpaConfigFiles = () => {
    showSpaConfigFiles.value = true
}

const onSpaConfigFilesError = (message: string) => {
    errorText.value = message
    errorSnackBar.value = true
}


const getMonitorDetails = async () => {
    try {
        loadingMonitorDetails.value = true
        monitorDetails.value = await useApi().getMonitorDetailsV1({ monitorId: props.monitorId })
    } catch (e) {
        console.error('Error fetching monitor details', e)
        if (e instanceof Error) {
            errorText.value = e.message
            errorSnackBar.value = true
        } else {
            errorText.value = JSON.stringify(e)
            errorSnackBar.value = true
        }
    } finally {
        loadingMonitorDetails.value = false
    }
}

const getMonitorFailures = async () => {
    handleApiError(
        async () => {
            loadingMonitorFailures.value = true
            const result = await useApi().getMonitorFailuresV1({
                monitorId: props.monitorId
            })
            monitorFailures.value = result.failures
        },
        async () => {
            loadingMonitorFailures.value = false
        }
    )
}

const enrichFailedExecutions = async (jobs: MonitorJobsV1) => {
    if (!jobs.executions?.length) return
    const failedWithoutDetails = jobs.executions
        .filter((e) => FAILED_STATUSES.has(e.status) && !e.statusDetails)
        .slice(0, 5)
    if (!failedWithoutDetails.length) return

    const details = await Promise.allSettled(
        failedWithoutDetails.map((e) =>
            useApi().getMonitorJobExecutionV1({
                monitorId: props.monitorId,
                jobId: e.jobId,
                executionNumber: e.executionNumber ?? 1
            })
        )
    )
    for (let i = 0; i < failedWithoutDetails.length; i++) {
        const result = details[i]
        if (result.status === 'fulfilled' && result.value.execution.statusDetails) {
            failedWithoutDetails[i].statusDetails = result.value.execution.statusDetails
        }
    }
}

const getMonitorJobs = async () => {
    loadingMonitorJobs.value = true
    await handleApiError(
        async () => {
            const jobs = await useApi().getMonitorJobsV1({ monitorId: props.monitorId })
            await enrichFailedExecutions(jobs)
            monitorJobs.value = jobs
        },
        async () => {
            loadingMonitorJobs.value = false
            if (monitorJobs.value?.pending?.length || !otaSentPending.value) {
                otaSentPending.value = false
            }
        }
    )
}

onMounted(() => {
    _loadData()
})

watch(
    () => props.monitorId,
    () => {
        _loadData()
    }
)

function _loadData() {
    getMonitorDetails()
    getMonitorJobs()
    getMonitorFailures()
    loadOtaOptions()
    breadCrumbsStore.$patch({
        items: [
            { text: 'Monitors' },
            { text: String(props.monitorId) }
        ]
    })
}

const sensorDetached = () => {
    _loadData()
}

const duration = (history: MonitorDetailsAttachmentHistoryV1): string => {
    let totalSeconds = 0
    if (history.current && history.startDate) {
        totalSeconds = differenceInSeconds(new Date(), history.startDate)
    } else if (history.startDate && history.endDate) {
        totalSeconds = differenceInSeconds(history.endDate, history.startDate)
    }
    if (totalSeconds <= 0) return ''
    const dur = intervalToDuration({ start: 0, end: totalSeconds * 1000 })
    return formatDuration(dur, { format: ['years', 'months', 'days'] }) || '0 days'
}

const totalTimeInWater = computed(() => {
    if (!monitorDetails.value) return 'n/a'
    if (monitorDetails.value.attachmentHistory) {
        let totalSeconds = 0
        for (const record of monitorDetails.value.attachmentHistory) {
            if (record.current && record.startDate) {
                totalSeconds += differenceInSeconds(new Date(), record.startDate)
            } else if (record.startDate && record.endDate) {
                totalSeconds += differenceInSeconds(record.endDate, record.startDate)
            }
        }
        const duration = intervalToDuration({ start: 0, end: totalSeconds * 1000 })
        return formatDuration(duration, { format: ['years', 'months', 'days'] }) || '0 days'
    }
    return ''
})

const updateSensorHistory = () => {
    sensorHistory.value?.fetchHistory()
}

const pickedSensor = async (sensor: GetSensorSearchResultV1) => {
    if (!monitorDetails.value) return
    try {
        await useApi().putMonitorSensorV1({
            monitorId: monitorDetails.value.monitorId,
            putMonitorSensorV1Request: {
                sensor: {
                    serialNumber: sensor.serialNumber,
                    manufacturedDateCode: sensor.manufacturedDate,
                    type: sensor.type
                }
            }
        })
        getMonitorDetails()
        updateSensorHistory()
    } finally {
        showAttachSensor.value = false
    }
}

const reportedFailure = () => {
    getMonitorFailures()
}

const onOtaSent = () => {
    otaSentPending.value = true
    getMonitorJobs()
}
</script>

<style scoped lang="scss">
.monitor-details-page {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 16px;
}

.monitor-header {
    margin-bottom: 24px;
}

.monitor-title {
    font-size: 1.5rem;
    font-weight: 600;
    letter-spacing: -0.01em;
}

.monitor-content {
    display: flex;
    flex-direction: column;
}

/* Info grid — replaces v-table for key/value pairs */
.info-grid {
    display: flex;
    flex-direction: column;
}

.info-row {
    display: flex;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    gap: 16px;

    &:last-child {
        border-bottom: none;
    }
}

.info-row--top {
    align-items: flex-start;
}

.info-label {
    flex: 0 0 160px;
    font-size: 0.85rem;
    font-weight: 500;
    color: #6b7280;
    white-space: nowrap;
}

.info-value {
    flex: 1;
    font-size: 0.9rem;
    min-width: 0;

    code {
        background: rgba(0, 0, 0, 0.04);
        padding: 2px 6px;
        border-radius: 4px;
        font-size: 0.82rem;
    }
}

/* Nav pills — compact links in the card title */
.nav-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 600;
    color: #fff;
    text-decoration: none;
    letter-spacing: 0.02em;
    transition: opacity 0.15s ease, transform 0.15s ease;

    &:hover {
        opacity: 0.85;
        transform: translateY(-1px);
    }
}

.nav-pill--gecko {
    background: linear-gradient(135deg, #2c5282, #3b82c4);
}

.nav-pill--analytics {
    background: linear-gradient(135deg, #2e7d32, #4caf50);
}

.nav-pill__icon {
    width: 16px;
    height: 16px;
}

.h-100 {
    height: 100%;
}

.mb-4 {
    margin-bottom: 16px;
}

.my-2 {
    margin-top: 8px;
    margin-bottom: 8px;
}
</style>
